import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { LOGOS, HERO_IMAGES, BRANCH_LIST } from "@/lib/brand";
function BranchCard({ b }) {
  return (
    <Link
      to={`/${b.key}`}
      data-testid={`home-branch-card-${b.key}`}
      className={`group relative overflow-hidden rounded-xl border bg-[#0e0e0e] p-8 hover-lift ${b.accentBorder}`}
      style={{ minHeight: 380 }}
    >
      <div
        className="absolute inset-x-0 -bottom-32 h-64 blur-3xl opacity-50 group-hover:opacity-80 transition-opacity"
        style={{ background: `radial-gradient(closest-side, ${b.accent}33, transparent)` }}
      />
      <div className="relative flex flex-col justify-between h-full">
        <div>
          <div className="flex items-start justify-between gap-4">
            <div className="rounded-xl overflow-hidden bg-white shadow-lg shadow-black/30 ring-1 ring-white/10">
              <img
                src={LOGOS[b.key]}
                alt={b.name}
                className="h-24 w-24 object-contain p-1.5"
                style={{ transform: `scale(${b.logoScale || 1})`, transformOrigin: "center" }}
              />
            </div>
            <ArrowUpRight
              size={24}
              className="text-zinc-500 group-hover:text-white group-hover:translate-x-1 group-hover:-translate-y-1 transition-all"
            />
          </div>
          <div className="mt-6">
            <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500">Pôle</div>
            <h3 className="mt-2 font-display text-3xl font-semibold tracking-tight">
              {b.name}
            </h3>
            <p className={`mt-2 text-sm font-medium ${b.accentText}`}>{b.tagline}</p>
            <p className="mt-4 text-sm text-zinc-400 leading-relaxed">{b.description}</p>
          </div>
        </div>
        <div className="mt-8 flex items-center gap-2 text-sm font-medium">
          <span style={{ color: b.accent }}>Découvrir</span>
          <span className="h-px w-10 transition-all group-hover:w-16" style={{ backgroundColor: b.accent }} />
        </div>
      </div>
    </Link>
  );
}

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative min-h-screen flex items-end pt-32 pb-20 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGES.groupe})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/80 to-[#0a0a0a]" />

        {/* Logo filigrane */}
        <div
          className="pointer-events-none absolute inset-0 flex items-center justify-end overflow-hidden"
          aria-hidden="true"
          data-testid="hero-logo-watermark"
        >
          <div className="relative -mr-[10%] sm:-mr-[6%] lg:mr-[2%]">
            {/* Halo lumineux */}
            <div
              className="absolute inset-0 blur-3xl opacity-40"
              style={{
                background:
                  "radial-gradient(closest-side, rgba(255,255,255,0.18), transparent 70%)",
              }}
            />
            <img
              src={LOGOS.groupe}
              alt=""
              className="relative w-[520px] sm:w-[640px] lg:w-[760px] max-w-[80vw] h-auto"
              style={{
                opacity: 0.18,
                filter: "drop-shadow(0 0 60px rgba(255,255,255,0.15))",
                mixBlendMode: "screen",
              }}
            />
          </div>
        </div>

        {/* Halo en bas-gauche pour ancrer la composition */}
        <div
          className="pointer-events-none absolute -bottom-32 -left-32 h-[420px] w-[420px] blur-3xl opacity-30 rounded-full"
          style={{
            background:
              "radial-gradient(closest-side, rgba(255,255,255,0.12), transparent 70%)",
          }}
        />

        <div className="absolute inset-0 grain" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 w-full">
          <div className="max-w-4xl fade-up">
            <div className="flex items-center gap-3 mb-8">
              <span className="h-px w-12 bg-white/40" />
              <span className="text-xs uppercase tracking-[0.3em] text-zinc-300">Groupe Equinoxe</span>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl md:text-7xl font-bold tracking-tighter leading-[0.95] text-balance">
              Trois expertises.<br />
              <span className="text-zinc-400">Une même exigence.</span>
            </h1>
            <p className="mt-8 max-w-2xl text-lg text-zinc-300 leading-relaxed">
              Conseil aux entreprises, sécurité d'événements et de locaux, entretien et propreté
              de vos espaces — le Groupe Equinoxe rassemble trois pôles complémentaires pour
              répondre à vos besoins professionnels et privés.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/contact"
                data-testid="hero-cta-devis"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-md bg-white text-black font-semibold hover:bg-zinc-200 transition-colors"
              >
                Demander un devis <ArrowUpRight size={18} />
              </Link>
              <Link
                to="/a-propos"
                data-testid="hero-cta-about"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-md border border-white/20 text-white hover:bg-white/5 transition-colors"
              >
                Découvrir le groupe
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PÔLES */}
      <section className="py-24 lg:py-32 max-w-7xl mx-auto px-6 lg:px-8" data-testid="home-poles-section">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-16">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-4">Nos pôles</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight max-w-2xl">
              Un écosystème de services au service de vos projets.
            </h2>
          </div>
          <p className="max-w-md text-zinc-400 text-sm leading-relaxed">
            Chaque pôle dispose de sa propre identité et de ses experts dédiés.
            Une seule porte d'entrée, trois savoir-faire complémentaires.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {BRANCH_LIST.map((b) => (
            <BranchCard key={b.key} b={b} />
          ))}
        </div>
      </section>

      {/* CHIFFRES */}
      <section className="py-24 border-y border-white/10 bg-[#070707]" data-testid="home-stats">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid sm:grid-cols-3 gap-12">
          {[
            { k: "3", l: "Pôles d'expertise" },
            { k: "20", l: "Années d'expérience" },
            { k: "24h", l: "Réponse à votre demande" },
          ].map((s, i) => (
            <div key={i} className="text-center">
              <div className="font-display text-6xl sm:text-7xl font-bold tracking-tighter">{s.k}</div>
              <div className="mt-3 text-xs uppercase tracking-[0.25em] text-zinc-500">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-32 max-w-7xl mx-auto px-6 lg:px-8 text-center" data-testid="home-cta-section">
        <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-bold tracking-tighter max-w-3xl mx-auto text-balance">
          Parlons de votre projet.
        </h2>
        <p className="mt-6 text-zinc-400 max-w-xl mx-auto">
          Une question, un besoin ponctuel ou un partenariat long terme ?
          Notre équipe vous répond sous 24h.
        </p>
        <Link
          to="/contact"
          data-testid="home-bottom-cta"
          className="inline-flex items-center gap-2 mt-10 px-8 py-4 rounded-md bg-white text-black font-semibold hover:bg-zinc-200 transition-colors"
        >
          Démarrer maintenant <ArrowUpRight size={18} />
        </Link>
      </section>
    </div>
  );
}
